import React from "react";
import { useTranslation } from "react-i18next";
import Header from "./Header";
import Footer from "./Footer";
import "./PrivacyPolicyPage.css";

const PrivacyPolicyPage: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="privacy-policy-page">
      <Header />
      <div className="privacy-policy-content">
        <div className="container">
          <div className="page-header">
            <h1 className="page-title">{t("privacy.title")}</h1>
            <p className="page-description">
              {t("privacy.description")}
            </p>
          </div>

          <div className="policy-sections">
            <div className="policy-main-section">
              <div className="section-card">
                <div className="policy-content">
                  <p className="last-updated">{t("privacy.lastUpdated")}</p>

                  <div className="policy-section">
                    <h2 className="section-title">{t("privacy.overview.title")}</h2>
                    <p>
                      {t("privacy.overview.content")}
                    </p>
                  </div>

                  <div className="policy-section">
                    <h2 className="section-title">{t("privacy.informationWeCollect.title")}</h2>
                    <p>
                      {t("privacy.informationWeCollect.content")}
                    </p>
                  </div>

                  <div className="policy-section">
                    <h2 className="section-title">{t("privacy.hubspot.title")}</h2>
                    <p>
                      {t("privacy.hubspot.description")}
                    </p>
                    <ul>
                      <li>{t("privacy.hubspot.contactInfo")}</li>
                      <li>{t("privacy.hubspot.pagesVisited")}</li>
                      <li>{t("privacy.hubspot.timeSpent")}</li>
                      <li>{t("privacy.hubspot.emailTracking")}</li>
                    </ul>
                    <p>
                      {t("privacy.hubspot.cookies")}{" "}
                      <a
                        href="https://legal.hubspot.com/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("privacy.hubspot.privacyPolicy")}
                      </a>
                      .
                    </p>
                  </div>

                  <div className="policy-section">
                    <h2 className="section-title">{t("privacy.googleAnalytics.title")}</h2>
                    <p>
                      {t("privacy.googleAnalytics.description")}
                    </p>
                    <ul>
                      <li>{t("privacy.googleAnalytics.pagesVisited")}</li>
                      <li>{t("privacy.googleAnalytics.browserInfo")}</li>
                      <li>{t("privacy.googleAnalytics.location")}</li>
                      <li>{t("privacy.googleAnalytics.referring")}</li>
                    </ul>
                    <p>
                      {t("privacy.googleAnalytics.ipAnonymization")}{" "}
                      <a
                        href="https://policies.google.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("privacy.googleAnalytics.privacyPolicy")}
                      </a>
                      .
                    </p>
                  </div>

                  <div className="policy-section">
                    <h2 className="section-title">{t("privacy.mapbox.title")}</h2>
                    <p>
                      {t("privacy.mapbox.description")}
                    </p>
                    <ul>
                      <li>{t("privacy.mapbox.locationData")}</li>
                      <li>{t("privacy.mapbox.ipAddress")}</li>
                      <li>{t("privacy.mapbox.usageStats")}</li>
                    </ul>
                    <p>
                      {t("privacy.mapbox.moreInfo")}{" "}
                      <a
                        href="https://www.mapbox.com/legal/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("privacy.mapbox.privacyPolicy")}
                      </a>
                      .
                    </p>
                  </div>

                  <div className="policy-section">
                    <h2 className="section-title">{t("privacy.gesslarDataUsage.title")}</h2>
                    <p>
                      {t("privacy.gesslarDataUsage.description")}
                    </p>
                    <ul>
                      <li>{t("privacy.gesslarDataUsage.provideServices")}</li>
                      <li>{t("privacy.gesslarDataUsage.improvePlatform")}</li>
                      <li>{t("privacy.gesslarDataUsage.communicate")}</li>
                      <li>{t("privacy.gesslarDataUsage.processTransactions")}</li>
                      <li>{t("privacy.gesslarDataUsage.promotional")}</li>
                      <li>{t("privacy.gesslarDataUsage.analyzeUsage")}</li>
                    </ul>
                  </div>

                  <div className="policy-section">
                    <h2 className="section-title">{t("privacy.yourRights.title")}</h2>
                    <p>
                      {t("privacy.yourRights.description")}
                    </p>
                    <ul>
                      <li>{t("privacy.yourRights.access")}</li>
                      <li>{t("privacy.yourRights.correct")}</li>
                      <li>{t("privacy.yourRights.delete")}</li>
                      <li>{t("privacy.yourRights.restrict")}</li>
                      <li>{t("privacy.yourRights.portability")}</li>
                      <li>{t("privacy.yourRights.withdraw")}</li>
                    </ul>
                    <p>
                      {t("privacy.yourRights.contact")}
                    </p>
                  </div>

                  <div className="policy-section">
                    <h2 className="section-title">{t("privacy.contactUs.title")}</h2>
                    <p>
                      {t("privacy.contactUs.description")}
                    </p>
                    <div className="contact-details">
                      <div className="contact-item">
                        <div className="contact-icon">📧</div>
                        <div className="contact-text">
                          <strong>{t("privacy.contactUs.email")}:</strong>
                          <a href="mailto:privacy@gesslargardens.com">
                            privacy@gesslargardens.com
                          </a>
                        </div>
                      </div>
                      <div className="contact-item">
                        <div className="contact-icon">🏢</div>
                        <div className="contact-text">
                          <strong>{t("privacy.contactUs.address")}:</strong>
                          {t("privacy.contactUs.companyName")}
                          <br />
                          {t("privacy.contactUs.streetAddress")}
                          <br />
                          {t("privacy.contactUs.cityStateZip")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="policy-side-section">
              <div className="section-card">
                <h2 className="section-title">{t("privacy.quickLinks.title")}</h2>
                <div className="quick-links">
                  <a href="/contact" className="quick-link">
                    <div className="quick-link-icon">📞</div>
                    <div className="quick-link-text">
                      <strong>{t("privacy.quickLinks.contactUs")}</strong>
                      <span>{t("privacy.quickLinks.contactDescription")}</span>
                    </div>
                  </a>
                  <a href="/terms" className="quick-link">
                    <div className="quick-link-icon">📋</div>
                    <div className="quick-link-text">
                      <strong>{t("privacy.quickLinks.termsOfService")}</strong>
                      <span>{t("privacy.quickLinks.readTerms")}</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
